import { Component, ElementRef, OnInit, ViewChild, signal, computed, AfterViewInit } from '@angular/core';

import { Observable } from 'rxjs';

import {
  faArrowRotateLeft,
  faCircleExclamation,
  faCircleInfo,
  faTrashCan,
  faUpload,
  faUserGraduate,
  faUserGroup,
  faCircleCheck,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';

import * as Sentry from '@sentry/angular';

import introJs from 'intro.js';

import { SchoolClass } from 'src/app/core/models/school-class.model';
import { BatchEnrolledStudentResponse, EnrollmentService } from 'src/app/core/services/enrollment.service';
import { StudentsService } from 'src/app/core/services/students.service';
import { SpreadsheetService } from 'src/app/core/services/spreadsheet.service';
import { SessionStorageService } from 'src/app/core/services/session-storage.service';
import {
  CustomerServiceSchema,
  SchoolAdminSchema,
  CustomerServiceBatchEnrollmentRecord,
  SchoolAdminBatchEnrollmentRecord,
  CustomerServiceBatchEnrollmentRecordSchema,
  SchoolAdminBatchEnrollmentRecordSchema,
  CustomerServiceRecordHeader,
  SchoolAdminServiceRecordHeader
} from '../../core/models/batch-enrollment.model';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { batchEnrollmentIntroSteps } from './batch-enrollment.intro-steps';

@Component({
  selector: 'wf-batch-enrollment',
  templateUrl: './batch-enrollment.component.html',
  styleUrls: ['../../core/styles/dialog.css', './batch-enrollment.component.css'],
  standalone: false
})
export class BatchEnrollmentComponent implements OnInit, AfterViewInit {
  fileName: string = 'Upload File';
  allGrades: string[] = [];
  selectedGrade: string = '';
  classes: SchoolClass[] = [];
  isCustomerService: boolean = false ;
  isUnavailable: boolean = false ;

  enrollIcon = CATEGORY_ICONS['enroll'] ;
  importIcon = faUpload ;
  removeIcon = faTrashCan ;
  undoIcon = faArrowRotateLeft ;
  studentIcon = faUserGraduate ;
  errorIcon = faCircleExclamation ;
  infoIcon = faCircleInfo;
  successIcon = faCircleCheck;
  downloadIcon = faDownload;

  protected readonly data = signal<CustomerServiceBatchEnrollmentRecord[] | SchoolAdminBatchEnrollmentRecord[]>([]);
  protected readonly headers = signal<string[]>([]);
  protected readonly parseErrors = signal<string[]>([]);
  protected readonly invalidBatch = computed(() => this.parseErrors().length > 0);
  protected readonly successMessage = signal<string | null>(null);
  protected readonly successTitle = signal<string>('Batch Enrollment Success');
  protected readonly showSuccessMessage = computed(() => this.successMessage() != null);
  protected readonly showPreview = computed(() => this.data().length > 0);
  protected readonly enableEnrollButton = computed(() => this.data().length > 0);
  protected readonly enableRemoveFileButton = computed(() => this.data().length > 0 || this.invalidBatch());
  protected readonly lastSuccessfulBatchEnrollmentID = signal<number | null>(null);
  protected readonly enableUndoButton = computed(() => this.lastSuccessfulBatchEnrollmentID() != null);
  protected readonly latestEnrolledStudentList = signal<BatchEnrolledStudentResponse[]>([]);
  protected readonly latestEnrollmentStudentCount = computed(() => this.latestEnrolledStudentList().length);

  // A few type guard methods for our different batch enrollment records
  protected readonly customerServiceBulkData = computed(() => {
    const tempData = this.data();
    return this.isCustomerService ? (tempData as CustomerServiceBatchEnrollmentRecord[]) : [];
  });

  protected readonly schoolAdminBulkData = computed(() => {
    const tempData = this.data();
    return !this.isCustomerService ? (tempData as SchoolAdminBatchEnrollmentRecord[]) : [];
  });

  introTour: any;

  @ViewChild('studentCSVUpload') studentCSVUpload!: ElementRef<HTMLInputElement>
  @ViewChild('undoConfirmationDialog') undoConfirmationDialog!: ElementRef<HTMLDialogElement>

  constructor(
    private enrollmentService: EnrollmentService,
    private studentsService: StudentsService,
    private spreadsheetService: SpreadsheetService,
    private sessionStorageService: SessionStorageService,
  ) { }

  ngOnInit(): void {
    const managementUser = this.sessionStorageService.userData();
    if (managementUser) {
      this.isCustomerService = managementUser.isCustomerServiceUser();
      this.isUnavailable = !this.isCustomerService && this.sessionStorageService.getSSOInfo() !== null;
    }

    this.loadLatestUndoableBatchEnrollment();
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: batchEnrollmentIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }
      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(batchEnrollmentIntroSteps);
    this.introTour.setOptions({
      steps: filteredSteps
    });
    this.introTour.start();
  }

  async handleFileInput(event: any): Promise<void> {
    this.resetState();
    const files = event.target.files;
    this.fileName = files[0].name;

    const parser$ = this.getParseObservable(files[0]);
    if (!parser$) {
      this.parseErrors.set([ 'This file type is not supported, please specify a .csv, .xlsx or .xls file' ]);
      return;
    }

    parser$.subscribe({
      next: ({ data: parsedData, errors }) => {
        this.data.set(parsedData);
        this.parseErrors.set(errors);
      },
      error: (err) => {
        this.parseErrors.set([err.message ?? 'Error parsing the specified CSV file']);

        Sentry.captureException(err, {
          tags: {
            section: 'batch-enroll',
            action: 'batch-enroll-file-parse',
            parseType: files[0].type === 'text/csv' ? 'csv (PapaParse)' : 'xlsx (read-excel-file)',
          }
        }) ;
      },
    });
  }

  removeFile(): void {
    this.studentCSVUpload.nativeElement.value = '';
    this.fileName = 'Upload File';
    this.resetState();
  }

  addStudents() {
    if (this.data().length < 1) return;
    this.enrollmentService.addStudentList(this.data(), this.isCustomerService ? 'cs' : 'sa').subscribe({
      next: (res) => {
        this.lastSuccessfulBatchEnrollmentID.set(res.batchEnrollmentID);
        this.latestEnrolledStudentList.set(res.enrolledStudentList ?? []);
        this.successTitle.set('Batch Enrollment Success');
        this.successMessage.set(`Successfully enrolled ${res.studentCount} Students`);
        this.data.set([]);
        this.parseErrors.set([]);
        this.fileName = 'Upload File';
      },
      error: (res: any) => {
        this.parseErrors.set(res.error.message.split('\n').filter((err: any) => err.length )) ;
      }
    });
  }

  undoBatchEnrollment() {
    this.undoConfirmationDialog.nativeElement.showModal();
  }

  closeUndoConfirmationDialog() {
    this.undoConfirmationDialog.nativeElement.close();
  }

  confirmUndoBatchEnrollment() {
    const batchEnrollmentID = this.lastSuccessfulBatchEnrollmentID();
    if (batchEnrollmentID == null) return;

    this.closeUndoConfirmationDialog();
    this.enrollmentService.undoBatchEnrollment(batchEnrollmentID).subscribe({
      next: () => {
        this.removeFile(); // Removes file and resets file upload state
        this.lastSuccessfulBatchEnrollmentID.set(null);
        this.latestEnrolledStudentList.set([]);
        this.successTitle.set('Batch Enrollment Undo Successful');
        this.successMessage.set('Successfully undid the most recent batch enrollment.');
      },
      error: (err: any) => {
        this.parseErrors.set([ 'Unable to Undo: batch enrolled students already have active sessions.' ]) ;

        Sentry.captureException(err, {
          tags: {
            section: 'batch-enrollment',
            action: 'undo-enrollment',
          }
        }) ;
      }
    });
  }

  private loadLatestUndoableBatchEnrollment(): void {
    this.enrollmentService.getLatestUndoableBatchEnrollment().subscribe({
      next: (batchEnrollment) => {
        this.lastSuccessfulBatchEnrollmentID.set(batchEnrollment?.batchEnrollmentID ?? null);
        this.latestEnrolledStudentList.set(batchEnrollment?.enrolledStudentList ?? []);
      },
      error: (err) => {
        Sentry.captureException(err, {
          tags: {
            section: 'batch-enrollment',
            action: 'latest-undoable-batch-enrollment',
          }
        }) ;
      }
    });
  }

  private resetState() {
    this.parseErrors.set([]);
    this.data.set([]);
    this.headers.set([]);
    this.successMessage.set(null);
  }

  private getParseObservable(file: File): Observable<{ headers: string[] ; data: any[] ; errors: string[] }> | null {
    const isCsv = this.spreadsheetService.isCsvFile(file);
    const isExcel = this.spreadsheetService.isExcelFile(file);

    if (isCsv) {
      return this.isCustomerService
        ? this.spreadsheetService.parseCsv(file, CustomerServiceBatchEnrollmentRecordSchema, CustomerServiceRecordHeader)
        : this.spreadsheetService.parseCsv(file, SchoolAdminBatchEnrollmentRecordSchema, SchoolAdminServiceRecordHeader);
    } else if (isExcel) {
      return this.isCustomerService
        ? this.spreadsheetService.parseExcel<CustomerServiceBatchEnrollmentRecord>(file, CustomerServiceSchema)
        : this.spreadsheetService.parseExcel<SchoolAdminBatchEnrollmentRecord>(file, SchoolAdminSchema);
    }

    return null;
  }
}
